/**
 * Sending a receipt to a thermal printer.
 *
 * There are three machines this runs on and each reaches a printer
 * differently:
 *
 *  - a till with the printer plugged into it, where the Python driver listens
 *    on localhost and does the talking;
 *  - a phone, where the printer is on Bluetooth and an app called RawBT does
 *    the talking, taking finished ESC/POS bytes off a link;
 *  - anything else, where the browser's own print dialog and whatever paper is
 *    in the office printer is better than nothing.
 *
 * Which one is used is a per-device choice, kept where the other per-device
 * choices are kept, and "automatic" picks the first of the three that answers.
 */

import { toBase64, toEscPos } from '@/lib/escpos';

const AGENT = 'http://127.0.0.1:9199';

/** The Android package RawBT registers, which the intent link names. */
const RAWBT_PACKAGE = 'ru.a402d.rawbtprinter';

/** A block the printer driver understands. */
export type PrintBlock =
  | { type: 'text'; text: string }
  | { type: 'center'; text: string }
  | { type: 'pair'; label: string; value: string }
  | { type: 'rule'; char?: string }
  | { type: 'big'; label?: string; text: string }
  | { type: 'item'; label: string; sku?: string; quantity?: string; price?: string; total?: string }
  | { type: 'signature'; label?: string }
  | { type: 'blank' };

export interface PrintDocument {
  /** 32 for a 58mm roll, 48 for 80mm. The driver defaults to 58mm. */
  width?: number;
  template?: string;
  blocks: PrintBlock[];
}

/**
 * The driver also takes the receipt payloads the API already builds, which it
 * lays out itself. Those go through unchanged rather than being rebuilt here.
 */
export type PrintPayload = PrintDocument | Record<string, unknown>;

export type PrintRoute = 'auto' | 'agent' | 'rawbt' | 'browser';

export type PrintOutcome =
  | { printed: true; via: 'agent' | 'rawbt' | 'browser' }
  | { printed: false; error: string };

const ROUTE_KEY = 'sp.printRoute';

/** How this device prints, as the person using it chose. */
export function printRoute(): PrintRoute {
  try {
    const stored = localStorage.getItem(ROUTE_KEY);
    return stored === 'agent' || stored === 'rawbt' || stored === 'browser' ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

export function setPrintRoute(route: PrintRoute): void {
  try {
    if (route === 'auto') localStorage.removeItem(ROUTE_KEY);
    else localStorage.setItem(ROUTE_KEY, route);
  } catch {
    /* storage unavailable — the choice simply will not outlive this tab */
  }
}

/** RawBT is an Android app, so nowhere else can be asked to use it. */
export function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

/** Whether a print agent is running on this machine. */
export async function agentAvailable(): Promise<boolean> {
  try {
    const response = await fetch(`${AGENT}/health`, { signal: AbortSignal.timeout(700) });
    return response.ok;
  } catch {
    // Not running, blocked, or no network stack — all mean "try something else".
    return false;
  }
}

/** What the agent says it is printing to, for a settings screen to show. */
export async function agentStatus(): Promise<{ running: boolean; printer?: string }> {
  try {
    const response = await fetch(`${AGENT}/health`, { signal: AbortSignal.timeout(700) });
    if (!response.ok) return { running: false };
    const body = (await response.json()) as { printer?: string };
    return { running: true, printer: body.printer };
  } catch {
    return { running: false };
  }
}

/** The printers the machine running the agent knows about. */
export async function agentPrinters(): Promise<string[]> {
  try {
    const response = await fetch(`${AGENT}/printers`, { signal: AbortSignal.timeout(2000) });
    if (!response.ok) return [];
    const body = (await response.json()) as { printers?: string[] };
    return body.printers ?? [];
  } catch {
    return [];
  }
}

/** Tells the agent which printer to use from now on, and remembers it there. */
export async function setAgentPrinter(printer: string): Promise<PrintOutcome> {
  try {
    const response = await fetch(`${AGENT}/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ printer }),
      signal: AbortSignal.timeout(4000),
    });

    if (response.ok) return { printed: true, via: 'agent' };
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    return { printed: false, error: body?.error ?? 'The driver would not accept that printer' };
  } catch {
    return {
      printed: false,
      error: 'The printer driver is not running on this computer.',
    };
  }
}

/**
 * Hands finished bytes to RawBT.
 *
 * The app takes them base64-encoded off a link. Android Chrome will not follow
 * a bare custom scheme from script, so the intent form — which names the
 * package — is used there; the plain scheme is the fallback for any browser
 * that registered the handler itself.
 */
function printViaRawbt(document: PrintDocument): PrintOutcome {
  const data = toBase64(toEscPos(document));

  const url = isAndroid()
    ? `intent:base64,${data}#Intent;scheme=rawbt;package=${RAWBT_PACKAGE};end;`
    : `rawbt:base64,${data}`;

  try {
    window.location.href = url;
    // The app either opens or the link does nothing; the browser reports
    // neither, so this is as much as can honestly be claimed.
    return { printed: true, via: 'rawbt' };
  } catch {
    return {
      printed: false,
      error: 'RawBT could not be opened. Install it from Google Play, then try again.',
    };
  }
}

function hasBlocks(document: PrintPayload): document is PrintDocument {
  return Array.isArray((document as PrintDocument).blocks);
}

async function printViaAgent(document: PrintPayload): Promise<PrintOutcome | null> {
  try {
    const response = await fetch(`${AGENT}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(document),
      signal: AbortSignal.timeout(8000),
    });

    if (response.ok) return { printed: true, via: 'agent' };

    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    // The agent answered and refused: the printer is off, out of paper, or
    // unknown. That is worth showing rather than silently trying something
    // else, which would print the same receipt twice.
    return { printed: false, error: body?.error ?? 'The printer refused the job' };
  } catch {
    // Not running. Null rather than a failure, so the caller can move on.
    return null;
  }
}

/**
 * Prints, by whichever route this device is set to.
 *
 * On "automatic" the local driver is tried first, then RawBT on a phone, then
 * the browser dialog — so a shop that has set nothing up still ends up with a
 * receipt in the customer's hand.
 */
export async function printDocument(
  document: PrintPayload,
  route: PrintRoute = printRoute(),
): Promise<PrintOutcome> {
  if (route === 'browser') {
    window.print();
    return { printed: true, via: 'browser' };
  }

  if (route === 'rawbt') {
    if (!hasBlocks(document)) {
      return {
        printed: false,
        error: 'This receipt cannot be sent to RawBT. Use the printer driver or the browser.',
      };
    }
    return printViaRawbt(document);
  }

  if (route === 'agent') {
    const result = await printViaAgent(document);
    return (
      result ?? {
        printed: false,
        error:
          'The printer driver is not running on this computer. Start it, or change how this '
          + 'device prints in Settings.',
      }
    );
  }

  const viaAgent = await printViaAgent(document);
  if (viaAgent) return viaAgent;

  if (isAndroid() && hasBlocks(document)) return printViaRawbt(document);

  window.print();
  return { printed: true, via: 'browser' };
}
